import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ReceiptSvg } from 'assets/images/receipt.svg';
import AnimatedSectionHeading from 'components/atoms/AnimatedSectionHeading';
import AnimatedSmallHeading from 'components/atoms/AnimatedSmallHeading';
import SectionParagraph from 'components/atoms/SectionParagraph';
import { breakPoints } from 'constant';
import RouteAnimWrapper from 'components/atoms/RouteAnimWrapper';
import AnimateIt from 'HOC/AnimateIt';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Axios from 'axios';
import Loader from 'components/atoms/Loader';

/**
 * @typedef {Object} FieldOption
 * @property {string} name
 * @property {string} type
 * @property {string} label
 */

/**
 * @typedef {Object} FieldDefinition
 * @property {string} name
 * @property {string} type
 * @property {string} label
 * @property {{ fields: FieldOption[] }} options
 */

/**
 * @typedef {Object} KursOgolnyValue
 * @property {string} typ_kursu
 * @property {string} ilosc_zajec_rocznie
 * @property {string} ilosc_zajec_tygodniowo
 * @property {string} koszt
 */

/**
 * @typedef {Object} KursEgzaminacyjnyValue
 * @property {string} typ_kursu
 * @property {string} ilosc_zajec_tygodniowo
 * @property {string} koszt
 */

/**
 * @typedef {Object} IndywidualnyValue
 * @property {string} ilosc_osob
 * @property {string} cena
 */

/**
 * @typedef {Object} KursGrupowy
 * @property {FieldDefinition} field
 * @property {KursOgolnyValue} value
 */

/**
 * @typedef {Object} KursEgzaminacyjny
 * @property {FieldDefinition} field
 * @property {KursEgzaminacyjnyValue} value
 */

/**
 * @typedef {Object} TrybIndywidualny
 * @property {FieldDefinition} field
 * @property {IndywidualnyValue} value
 */

/**
 * @typedef {Object} Cennik
 * @property {string} tytul
 * @property {string} podtytul
 * @property {KursGrupowy[]} kursyGrupowe
 * @property {KursEgzaminacyjny[]} przygotowanieDoEgzaminow
 * @property {TrybIndywidualny[]} trybIndywidualny
 * @property {string} oplataZaKursTekst
 * @property {string} [_mby]
 * @property {string} [_by]
 */

const PriceList = () => {
  const { t } = useTranslation();

  /** @type {[Cennik, Function]} */
  const [priceList, setPriceList] = useState(undefined);

  useEffect(() => {
    (async () => {
      const { data } = await Axios.get('api/singletons/get/cennik');
      setPriceList(data);
    })();
  }, []);

  const groupFields =
    priceList?.kursyGrupowe?.[0]?.field?.options?.fields ?? [];
  const prepareFields =
    priceList?.przygotowanieDoEgzaminow?.[0]?.field?.options?.fields ?? [];
  const individualFields =
    priceList?.trybIndywidualny?.[0]?.field?.options?.fields ?? [];

  return (
    <RouteAnimWrapper>
      <Helmet>
        <title>{t('price_list.heading1')}</title>
      </Helmet>
      {priceList ? (
        <GroupSection>
          <TopWrapper>
            <AnimatedSectionHeading as="h1">
              {priceList?.tytul}
            </AnimatedSectionHeading>
            <AnimatedSmallHeading as="h4">
              {priceList?.podtytul}
            </AnimatedSmallHeading>
            <ReceiptSvg />
          </TopWrapper>

          <AnimatedSmallHeading
            style={{ padding: '0 1.5rem', margin: '2rem 0 0 0' }}
          >
            {t('price_list.heading2')}
          </AnimatedSmallHeading>

          <ScrollX>
            <Table className="with-break primary-table">
              <thead>
                <tr>
                  {groupFields.map((field) => (
                    <th key={field.name}>{field.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {priceList?.kursyGrupowe?.map((item, i) => (
                  <tr key={i}>
                    {groupFields.map((field) => (
                      <td key={field.name} data-th={field.label}>
                        {item.value[field.name]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </ScrollX>

          <AnimatedSmallHeading style={{ padding: '0 1.5rem' }}>
            {t('price_list.prepare')}
          </AnimatedSmallHeading>

          <ScrollX>
            <Table className="with-break">
              <thead>
                <tr>
                  {prepareFields.map((field) => (
                    <th key={field.name}>{field.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {priceList?.przygotowanieDoEgzaminow?.map((item, i) => (
                  <tr key={i}>
                    {prepareFields.map((field) => (
                      <td key={field.name} data-th={field.label}>
                        {item.value[field.name]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </ScrollX>

          <IndividualSection>
            <AnimatedSmallHeading
              style={{ padding: '2rem 1.5rem 0', margin: 0 }}
            >
              {t('price_list.heading5')}
            </AnimatedSmallHeading>

            <ScrollX>
              <Table
                style={{ paddingBottom: 0 }}
                className="without-break primary-table"
              >
                <thead>
                  <tr>
                    {individualFields.map((field) => (
                      <th key={field.name}>{field.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {priceList?.trybIndywidualny?.map((item, i) => (
                    <tr key={i}>
                      {individualFields.map((field) => (
                        <td key={field.name} data-th={field.label}>
                          {item.value[field.name]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ScrollX>

            <SectionParagraph>{t('price_list.text6')}</SectionParagraph>
            <SectionParagraph>{t('price_list.text7')}</SectionParagraph>
          </IndividualSection>

          <AnimatedSmallHeading style={{ padding: '0 1.5rem' }}>
            {t('price_list.heading3')}
          </AnimatedSmallHeading>

          <Content
            dangerouslySetInnerHTML={{ __html: priceList.oplataZaKursTekst }}
          />

          <AnimatedSmallHeading style={{ padding: '0 1.5rem' }}>
            {t('price_list.heading4')}
          </AnimatedSmallHeading>

          <InstallmentGrid>
            <InstallmentItem>
              <InstallmentNumber>
                1 {t('price_list.instalment')}
              </InstallmentNumber>
              <InstallmentDate>{t('price_list.at_sign_up')}</InstallmentDate>
            </InstallmentItem>
            {Array.from({ length: 9 }, (_, k) => (
              <InstallmentItem key={k}>
                <InstallmentNumber>
                  {k + 2} {t('price_list.instalment')}
                </InstallmentNumber>
                <InstallmentDate>
                  {t('price_list.pay_by')}
                  <br />
                  15.{k < 3 ? 10 + k : `0${k - 2}`}
                </InstallmentDate>
              </InstallmentItem>
            ))}
            <AnimateIt
              initial={{ x: '100%', opacity: 0 }}
              variants={{
                visible: () => ({
                  x: 0,
                  opacity: 1,
                  transition: { delay: 0.2 },
                }),
              }}
            >
              <InstallmentItem>
                <ZeroPercent>{t('price_list.zero_percent')}</ZeroPercent>
              </InstallmentItem>
            </AnimateIt>
          </InstallmentGrid>

          <AnimatedSmallHeading>
            {t('price_list.heading6')}
          </AnimatedSmallHeading>
          <SectionParagraph style={{ whiteSpace: 'pre-line' }}>
            {t('price_list.text8')}{' '}
            <span style={{ whiteSpace: 'nowrap' }}>
              {t('price_list.text9')}
            </span>
          </SectionParagraph>
        </GroupSection>
      ) : (
        <Loader style={{ margin: '2rem auto' }} />
      )}
    </RouteAnimWrapper>
  );
};

const Content = styled.div`
  max-width: 44rem;
  margin: 1rem auto;

  & a {
    color: ${({ theme }) => theme.secondary};
    font-weight: 600;
  }

  p {
    margin: 1rem auto;
    font-size: 1rem;
    padding: 0 1.5rem 1rem;
    color: ${({ theme }) => theme.txtColor};
    max-width: 44rem;
    line-height: 1.6;
  }

  & a:hover {
    opacity: 0.8;
  }
`;

const IndividualSection = styled.section`
  max-width: 75rem;
  margin: 0 auto;
`;

const InstallmentGrid = styled.div`
  padding: 1.5rem;
  display: grid;
  grid-gap: 0.75rem;
  grid-template-columns: 1fr 1fr 1fr;

  @media (min-width: 47rem) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  }
`;

const ZeroPercent = styled.div`
  padding: 1rem;
  border-radius: 0.25rem;
  font-size: 1.4rem;
  text-align: center;
  background-color: ${({ theme }) => theme.txtColor};
  font-weight: 600;
  color: #ffffff;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InstallmentItem = styled(motion.div)`
  border-radius: 0.25rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  box-shadow: 0 16px 24px -16px rgba(59, 142, 184, 0.25);

  &:last-child {
    grid-column: 2/-1;
  }

  @media (min-width: 47rem) {
    &:last-child {
      grid-column: initial;
    }
  }
`;

const InstallmentNumber = styled.div`
  background-color: ${({ theme }) => theme.txtColor};
  color: white;
  text-align: center;
  font-weight: 600;
  padding: 1rem 0.5rem;
`;

const InstallmentDate = styled.div`
  text-align: center;
  flex: 1;
  background-color: #ffffff;
  color: ${({ theme }) => theme.txtColor};
  padding: 1rem;
  white-space: pre-line;
`;

const GroupSection = styled.section`
  max-width: 75rem;
  margin: 0 auto;
`;

const ScrollX = styled.div`
  overflow-x: auto;
  width: 100%;
`;

const TopWrapper = styled.div`
  max-width: 30rem;
  margin: 0 auto;
  svg {
    max-width: 10rem;
    width: 65%;
    height: auto;
    display: block;
    margin: 0rem auto 1rem 1.5rem;
  }
`;

const Table = styled.table`
  border-collapse: collapse;
  padding: 1.5rem;
  display: inline-block;
  min-width: 18rem;
  width: 100%;
  &.primary-table {
    th,
    td:first-child {
      background-color: ${({ theme }) => theme.primary};
    }

    th,
    td {
      border-color: ${({ theme }) => theme.primary};
    }
  }

  th,
  td:first-child {
    font-weight: 600;
    color: white;
    background-color: ${({ theme }) => theme.secondary};
    text-align: left;
    text-transform: uppercase;
    font-size: 1rem;
  }

  th,
  td {
    border: 1px solid ${({ theme }) => theme.secondary};
    border-collapse: collapse;
    padding: 1rem 1.15rem;
    text-align: left;
  }

  tbody {
    display: block;
  }

  tbody tr:not(:last-child) {
    margin-bottom: 1.5rem;
  }

  tbody tr {
    display: block;
    width: 100%;
  }

  th:not(:last-child),
  td:not(:last-child) {
    border-bottom: none;
  }

  td {
    text-align: left;
    color: ${({ theme }) => theme.txtColor};
    font-size: 1.25rem;
    display: block;
    &:before {
      content: '';
      vertical-align: middle;
      display: inline-block;
      height: 100%;
    }

    &:not(:first-child):before {
      content: attr(data-th) ': ';
      font-weight: 600;
      width: 100%;
      display: block;
      padding-bottom: 0.75rem;
      font-size: 1rem;
    }
  }

  tbody {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 1rem;

    @media (min-width: ${breakPoints.beforeTablet}) {
      grid-template-columns: 1fr 1fr;
    }

    @media (min-width: ${breakPoints.tablet}) {
      grid-template-columns: 1fr 1fr 1fr 1fr;
    }
  }
  &.without-break {
    thead {
      border: 0 !important;
      clip: rect(1px, 1px, 1px, 1px) !important;
      -webkit-clip-path: inset(50%) !important;
      clip-path: inset(50%) !important;
      height: 1px !important;
      margin: -1px !important;
      overflow: hidden !important;
      padding: 0 !important;
      position: absolute !important;
      width: 1px !important;
      white-space: nowrap !important;
    }
  }

  &.with-break {
    @media (min-width: ${breakPoints.beforeTablet}) {
      tr:last-child td:first-child {
        height: 4.8125rem;
      }
    }
    @media (max-width: 46.99rem) {
      thead {
        border: 0 !important;
        clip: rect(1px, 1px, 1px, 1px) !important;
        -webkit-clip-path: inset(50%) !important;
        clip-path: inset(50%) !important;
        height: 1px !important;
        margin: -1px !important;
        overflow: hidden !important;
        padding: 0 !important;
        position: absolute !important;
        width: 1px !important;
        white-space: nowrap !important;
      }
    }

    @media (min-width: 47rem) {
      min-width: 47rem;

      th,
      td {
        text-align: center;
        display: table-cell;
      }

      th:not(:last-child),
      td:not(:last-child) {
        border-bottom: 1px solid ${({ theme }) => theme.secondary};
      }

      &.primary-table {
        th:not(:last-child),
        td:not(:last-child) {
          border-bottom: 1px solid ${({ theme }) => theme.primary};
        }
      }

      th,
      td:first-child {
        font-size: 0.85rem;
        text-align: left;
      }

      td {
        &:not(:first-child):before {
          display: none;
        }
      }

      tbody {
        display: table-row-group;
      }

      tbody tr {
        display: table-row;
        margin-bottom: 0;
      }
    }
  }
`;

export default PriceList;
